import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  Renderer2
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { LetDirective } from '@ngrx/component';
import { PROFILE_COMPONENTS_COMMON_LOCALIZATION_SCOPE } from '../../models/constants';
import { CopyActionIconClasses, enterTransition } from '../../models/animation';
import { ProfileLinkProvider, ProfileType } from '@capital-access/profiles/common';
import { CommonLocalizationModule, LocalizationService } from '@capital-access/common/localization';
import { CommonFormattingModule } from '@capital-access/common/formatting';
import {
  Breakpoint,
  FireflyChipModule,
  FireflyDrawerContent,
  FireflyDrawerModule,
  FireflyFormattingModule,
  FireflyListModule,
  iconAnimation
} from '@capital-access/firefly/components';

import { ProfilePreviewLinkModule } from '../preview-link/profile-preview-link.module';
import { OrderPortfolioManagersPipe } from '../portfolio-managers/order-portfolio-managers.pipe';
import { EsgComponentsModule } from '@capital-access/esg/components';
import { ProfilesComponentsCommonModule } from '../../profiles-components-common.module';
import { FundPortfolioManager } from '../../models/fund-portfolio-managers.model';

@Component({
  selector: 'ca-portfolio-managers-drawer',
  templateUrl: './portfolio-managers-drawer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    LetDirective,
    CommonModule,
    CommonLocalizationModule,
    CommonFormattingModule,
    FireflyChipModule,
    FireflyDrawerModule,
    ReactiveFormsModule,
    FireflyFormattingModule,
    FireflyListModule,
    ProfilePreviewLinkModule,
    ProfilesComponentsCommonModule,
    OrderPortfolioManagersPipe,
    EsgComponentsModule
  ],
  animations: [iconAnimation]
})
export class PortfolioManagersDrawerComponent extends FireflyDrawerContent<any> implements OnInit {
  LOCALIZATION_SCOPE = PROFILE_COMPONENTS_COMMON_LOCALIZATION_SCOPE;
  ProfileType = ProfileType;
  hideDelay = 1000;

  @Input() portfolioManagers: FundPortfolioManager[] = [];
  @Input() fundId!: number;

  copyStatus: {
    [id: number]: {
      showCheckIcon: boolean;
      showCopyIcon: boolean;
    };
  } = {};
  private readonly transitionOnEnter = `background-color ${enterTransition}ms ease-out`;
  iconClass: CopyActionIconClasses = CopyActionIconClasses.Copy;

  get mobile(): boolean {
    return window.innerWidth < Breakpoint.Sm;
  }
  constructor(
    public localization: LocalizationService,
    private linkProvider: ProfileLinkProvider,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.portfolioManagers.forEach((_item, index) => {
      this.copyStatus[index] = {
        showCheckIcon: false,
        showCopyIcon: this.mobile
      };
    });
  }

  override onInjectInputs(inputs: any) {
    this.portfolioManagers = inputs.portfolioManagers || [];
    this.fundId = inputs.fundId;
  }

  getViewInTableLink() {
    return this.linkProvider.getFundProfileLink(this.fundId) + '/fund-managers';
  }

  private addCheckedStatus(index: number) {
    this.copyStatus[index] = { showCheckIcon: true, showCopyIcon: false };
    this.renderer.setStyle(this.elementRef.nativeElement, 'transition', this.transitionOnEnter);
    this.changeDetectorRef.markForCheck();

    if (this.mobile) this.startHideTimeout(index);
  }

  copy(email: string, index: number) {
    navigator.clipboard
      .writeText(email)
      .then(() => {
        this.addCheckedStatus(index);
      })
      .catch(error => console.error(`Failed to copy text error: ${error}`));
  }

  onMouseEnter(index: number) {
    if (!this.mobile) {
      this.copyStatus[index].showCheckIcon == true
        ? (this.copyStatus[index].showCopyIcon = false)
        : (this.copyStatus[index].showCopyIcon = true);
    }
  }
  onMouseLeave(index: number) {
    if (!this.mobile) {
      this.copyStatus[index].showCopyIcon = false;
      this.copyStatus[index].showCheckIcon = false;
    }
  }

  private startHideTimeout(index: number) {
    setTimeout(() => {
      this.copyStatus[index] = { showCheckIcon: false, showCopyIcon: true };
      this.renderer.removeStyle(this.elementRef.nativeElement, 'transition');
      this.changeDetectorRef.markForCheck();
    }, this.hideDelay);
  }
}
